import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Checkbox,
  DatePicker,
  FilterToolbarSearch,
  FormLabel,
  filterToolbarClinicClassName,
  filterToolbarControlLgClassName,
  filterToolbarControlSmClassName,
  filterToolbarSearchGrowClassName,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsCount,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import OrderManagerTab from '../components/order-manager/OrderManagerTab';
import { ClinicSelect } from '../components/clinic/ClinicSelect';
import { ExcludePreTakeoverFilterCheckbox } from '../components/filters/ExcludePreTakeoverFilterCheckbox';
import {
  getOrderManagerCounts,
  getOrderManagerFilterOptions,
} from '../services/api';
import type {
  OrderManagerPhysicianOption,
  OrderTypeOption,
} from '../services/api/types.gen';
import { formatTabCount } from '../components/billing/formatTabCount';
import { useStore } from '../store';
import {
  orderManagerDateRangeInvalid,
  orderManagerFilterQuery,
  orderManagerFilterRemountKey,
  orderManagerHasNarrowingFilters,
} from '../store/slices/orderManagerSlice';
import { useShallow } from 'zustand/react/shallow';
import { formatDateToISO, parseISOToLocalDate } from '../utils/dateUtils';

const ORDER_TABS = [
  // Orders still to be signed and submitted on their task before they can be sent.
  { id: 'not-ready', label: 'Not Ready' },
  { id: 'to-be-sent', label: 'To Be Sent' },
  { id: 'pending-signature', label: 'Pending Physician Signature' },
  // Id stays 'received' to preserve deep links and the STATUS_MAP wire format.
  { id: 'received', label: 'Completed' },
] as const;

type TabId = (typeof ORDER_TABS)[number]['id'];

const ALL_PHYSICIANS_VALUE = 'all';
const UNASSIGNED_PHYSICIAN_VALUE = 'unassigned';
const ALL_ORDER_TYPES_VALUE = 'all';

const Orders: React.FC = () => {
  const {
    hasPermission,
    orderManagerFilters,
    setOrderManagerFilters,
    agencyClinics,
    fetchAgencyClinics,
  } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      orderManagerFilters: state.orderManagerFilters,
      setOrderManagerFilters: state.setOrderManagerFilters,
      agencyClinics: state.agencyClinics,
      fetchAgencyClinics: state.fetchAgencyClinics,
    }))
  );

  const [searchParams] = useSearchParams();
  const highlightOrderId = searchParams.get('highlightOrder') ?? undefined;
  const tabParam = searchParams.get('tab');
  const initialTabId: TabId = ORDER_TABS.some(t => t.id === tabParam)
    ? (tabParam as TabId)
    : ORDER_TABS[0].id;

  const [activeTab, setActiveTab] = useState<TabId>(initialTabId);
  const [mountedTabIds, setMountedTabIds] = useState<Set<TabId>>(
    () => new Set([initialTabId])
  );
  const [refetchKey, setRefetchKey] = useState(0);
  const [tabCounts, setTabCounts] = useState<Record<TabId, number | null>>({
    'not-ready': null,
    'to-be-sent': null,
    'pending-signature': null,
    received: null,
  });
  const [searchInput, setSearchInput] = useState(
    orderManagerFilters.patientSearch
  );
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countsRequestIdRef = useRef(0);
  const [physicians, setPhysicians] = useState<OrderManagerPhysicianOption[]>(
    []
  );
  const [orderTypes, setOrderTypes] = useState<OrderTypeOption[]>([]);

  const filterQuery = useMemo(
    () => orderManagerFilterQuery(orderManagerFilters),
    [orderManagerFilters]
  );
  const filterRemountKey = useMemo(
    () => orderManagerFilterRemountKey(orderManagerFilters),
    [orderManagerFilters]
  );
  const hasNarrowingFilters =
    orderManagerHasNarrowingFilters(orderManagerFilters);
  const dateRangeInvalid = orderManagerDateRangeInvalid(orderManagerFilters);

  const clinicNameById = useMemo(
    () => new Map(agencyClinics.map(clinic => [clinic.id, clinic.name])),
    [agencyClinics]
  );

  // Drop physicians from a previous clinic while the new options request is in
  // flight, and label duplicates when "All clinics" is selected.
  const visiblePhysicians = useMemo(() => {
    if (!orderManagerFilters.clinicId) return physicians;
    return physicians.filter(
      physician => physician.clinic_id === orderManagerFilters.clinicId
    );
  }, [physicians, orderManagerFilters.clinicId]);

  const fetchCounts = useCallback(async () => {
    const requestId = ++countsRequestIdRef.current;
    const res = await getOrderManagerCounts({
      query: filterQuery,
    });
    if (requestId !== countsRequestIdRef.current) return;
    if (res.error || !res.data) {
      setTabCounts({
        'not-ready': null,
        'to-be-sent': null,
        'pending-signature': null,
        received: null,
      });
      return;
    }
    setTabCounts({
      'not-ready': res.data.not_ready,
      'to-be-sent': res.data.to_be_sent,
      'pending-signature': res.data.pending_signature,
      received: res.data.completed,
    });
  }, [filterQuery]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, refetchKey]);

  useEffect(() => {
    fetchAgencyClinics();
  }, [fetchAgencyClinics]);

  // Drop a persisted clinic that no longer exists in the agency list.
  useEffect(() => {
    if (!orderManagerFilters.clinicId || agencyClinics.length === 0) return;
    if (
      agencyClinics.some(clinic => clinic.id === orderManagerFilters.clinicId)
    ) {
      return;
    }
    setOrderManagerFilters({
      clinicId: null,
      clinicPhysicianId: null,
      unassignedPhysician: false,
    });
  }, [agencyClinics, orderManagerFilters.clinicId, setOrderManagerFilters]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getOrderManagerFilterOptions({
        query: {
          clinic_id: orderManagerFilters.clinicId || undefined,
        },
      });
      if (cancelled || !res.data) return;
      setPhysicians(res.data.physicians);
      setOrderTypes(res.data.order_types);

      const current = useStore.getState().orderManagerFilters;
      if (
        current.clinicPhysicianId &&
        !current.unassignedPhysician &&
        !res.data.physicians.some(
          physician => physician.id === current.clinicPhysicianId
        )
      ) {
        setOrderManagerFilters({ clinicPhysicianId: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderManagerFilters.clinicId, setOrderManagerFilters]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
  }, []);

  const location = useLocation();
  const initialKeyRef = useRef(location.key);

  // Refetch order lists on any navigation back to this page.
  // location.key is unique per navigation entry, so it changes even when returning
  // to the same path (handles component unmount/remount after signing an order).
  useEffect(() => {
    if (location.key === initialKeyRef.current) return;
    setRefetchKey(k => k + 1);
  }, [location.key]);

  const handleTabChange = useCallback((value: string) => {
    const id = value as TabId;
    setActiveTab(id);
    setMountedTabIds(prev => new Set(prev).add(id));
  }, []);

  const handlePatientSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setOrderManagerFilters({ patientSearch: value });
      searchTimerRef.current = null;
    }, 300);
  };

  const physicianSelectValue = orderManagerFilters.unassignedPhysician
    ? UNASSIGNED_PHYSICIAN_VALUE
    : (orderManagerFilters.clinicPhysicianId ?? ALL_PHYSICIANS_VALUE);

  const hasAccess = hasPermission('order-manager:view');

  if (!hasAccess) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access the Order Manager. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        <TabsList className='flex w-fit max-w-full flex-wrap max-sm:h-auto'>
          {ORDER_TABS.map(tab => (
            <TabsTrigger key={tab.id} value={tab.id}>
              <span className='inline-flex items-center gap-x-1'>
                <span>{tab.label}</span>
                <TabsCount
                  label={tab.label}
                  value={formatTabCount(tabCounts[tab.id])}
                />
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Filters sit in a row above the table, like NOA Manager; below `md`
            they fall back to a compact 2-col grid. Do not change the tab bar. */}
        <div className='grid grid-cols-2 gap-x-3 gap-y-3 md:flex md:flex-wrap md:items-end'>
          <FilterToolbarSearch
            value={searchInput}
            onChange={handlePatientSearchChange}
            label='Search'
            placeholder='Name or MRN...'
            containerClassName={`${filterToolbarSearchGrowClassName} col-span-2`}
            className='w-full'
          />

          <div className={filterToolbarClinicClassName}>
            <FormLabel htmlFor='order-clinic-filter' showTooltip={false}>
              Clinic
            </FormLabel>
            <ClinicSelect
              inputId='order-clinic-filter'
              className='w-full'
              clinics={agencyClinics}
              includeAllOption
              allOptionValue='all'
              allOptionLabel='All clinics'
              value={orderManagerFilters.clinicId ?? 'all'}
              onChange={e => {
                const v = e.target.value;
                setOrderManagerFilters({
                  clinicId: v === 'all' ? null : v,
                  // Clinic change drops a physician that may not belong there.
                  clinicPhysicianId: null,
                  unassignedPhysician: false,
                });
              }}
              placeholder='All clinics'
            />
          </div>

          <div className={filterToolbarControlSmClassName}>
            <FormLabel htmlFor='order-type-filter' showTooltip={false}>
              Type
            </FormLabel>
            <Select
              value={orderManagerFilters.orderType ?? ALL_ORDER_TYPES_VALUE}
              onValueChange={v => {
                setOrderManagerFilters({
                  orderType: v === ALL_ORDER_TYPES_VALUE ? null : v,
                });
              }}
            >
              <SelectTrigger
                id='order-type-filter'
                selectSize='default'
                className='w-full'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ORDER_TYPES_VALUE}>All types</SelectItem>
                {orderTypes.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className={filterToolbarControlLgClassName}>
            <FormLabel htmlFor='order-physician-filter' showTooltip={false}>
              Physician
            </FormLabel>
            <Select
              value={physicianSelectValue}
              onValueChange={v => {
                if (v === ALL_PHYSICIANS_VALUE) {
                  setOrderManagerFilters({
                    clinicPhysicianId: null,
                    unassignedPhysician: false,
                  });
                } else if (v === UNASSIGNED_PHYSICIAN_VALUE) {
                  setOrderManagerFilters({
                    clinicPhysicianId: null,
                    unassignedPhysician: true,
                  });
                } else {
                  setOrderManagerFilters({
                    clinicPhysicianId: v,
                    unassignedPhysician: false,
                  });
                }
              }}
            >
              <SelectTrigger
                id='order-physician-filter'
                selectSize='default'
                className='w-full'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PHYSICIANS_VALUE}>
                  All physicians
                </SelectItem>
                <SelectItem value={UNASSIGNED_PHYSICIAN_VALUE}>
                  Unassigned
                </SelectItem>
                {visiblePhysicians.map(physician => {
                  const clinicName = clinicNameById.get(physician.clinic_id);
                  const label =
                    !orderManagerFilters.clinicId && clinicName
                      ? `${physician.name} (${clinicName})`
                      : physician.name;
                  return (
                    <SelectItem key={physician.id} value={physician.id}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className={filterToolbarControlSmClassName}>
            <FormLabel htmlFor='order-date-from' showTooltip={false}>
              Order date from
            </FormLabel>
            <DatePicker
              id='order-date-from'
              className='w-full'
              typable
              showClearButton
              clearButtonLabel='Clear order date from'
              date={
                orderManagerFilters.orderDateFrom
                  ? parseISOToLocalDate(orderManagerFilters.orderDateFrom)
                  : undefined
              }
              onDateChange={d => {
                setOrderManagerFilters({
                  orderDateFrom: d ? formatDateToISO(d) : '',
                });
              }}
            />
          </div>

          <div className={filterToolbarControlSmClassName}>
            <FormLabel htmlFor='order-date-to' showTooltip={false}>
              Order date to
            </FormLabel>
            <DatePicker
              id='order-date-to'
              className='w-full'
              typable
              showClearButton
              clearButtonLabel='Clear order date to'
              date={
                orderManagerFilters.orderDateTo
                  ? parseISOToLocalDate(orderManagerFilters.orderDateTo)
                  : undefined
              }
              onDateChange={d => {
                setOrderManagerFilters({
                  orderDateTo: d ? formatDateToISO(d) : '',
                });
              }}
            />
          </div>

          {dateRangeInvalid ? (
            <p className='col-span-2 text-xs text-muted-foreground md:basis-full'>
              Order date to must be on or after from. The range is not applied
              until both dates are valid.
            </p>
          ) : null}

          {/* h-8 matches Search / default selects under items-end. */}
          <div className='flex h-8 items-center gap-2 self-end col-span-2 md:col-span-1'>
            <Checkbox
              id='order-hide-test'
              checked={orderManagerFilters.hideTest}
              onCheckedChange={(checked: boolean) => {
                setOrderManagerFilters({ hideTest: !!checked });
              }}
              checkboxSize='sm'
            />
            <label
              htmlFor='order-hide-test'
              className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
            >
              Hide test patients
            </label>
          </div>

          <div className='flex h-8 items-center gap-2 self-end col-span-2 md:col-span-1'>
            <Checkbox
              id='order-hide-imported'
              checked={orderManagerFilters.hideImportedPatients}
              onCheckedChange={(checked: boolean) => {
                setOrderManagerFilters({ hideImportedPatients: !!checked });
              }}
              checkboxSize='sm'
            />
            <label
              htmlFor='order-hide-imported'
              className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
            >
              Filter out imported patients
            </label>
          </div>

          <ExcludePreTakeoverFilterCheckbox
            id='order-exclude-pre-takeover'
            checked={orderManagerFilters.excludePreTakeover}
            onCheckedChange={checked => {
              setOrderManagerFilters({ excludePreTakeover: checked });
            }}
          />
        </div>

        <Card>
          <CardContent className='py-0'>
            {ORDER_TABS.map(tab => (
              <TabsContent key={tab.id} value={tab.id} className='m-0'>
                {mountedTabIds.has(tab.id) ? (
                  // Remount when filters flip so the tab starts at page 1 and
                  // fetches once (avoids a stale page-N request racing a
                  // separate setPage(1) effect).
                  <OrderManagerTab
                    key={`${tab.id}-${filterRemountKey}`}
                    tabId={tab.id}
                    refetchKey={refetchKey}
                    filterQuery={filterQuery}
                    hasNarrowingFilters={hasNarrowingFilters}
                    onAfterRefetch={fetchCounts}
                    highlightOrderId={
                      tab.id === activeTab ? highlightOrderId : undefined
                    }
                  />
                ) : null}
              </TabsContent>
            ))}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default Orders;
